/**
 * El conteo del cierre: cuántos billetes y monedas hay en la caja. El total lo recalcula el
 * servidor y esta cifra tiene que dar el mismo número; un pgTAP cruza las dos. Suma TODAS las
 * claves del conteo, no solo las de `DENOMINACIONES_COP`, igual que el servidor.
 */
export function contarEfectivo(conteo: Record<string, number>): number {
  return Object.entries(conteo).reduce((suma, [denominacion, unidades]) => {
    const valor = Number(denominacion);
    const cuantas = Number(unidades);
    if (!Number.isFinite(valor) || !Number.isFinite(cuantas)) return suma;
    return suma + valor * cuantas;
  }, 0);
}

/**
 * El subtotal de un montón: los billetes o las monedas. Cuenta **solo** las denominaciones que se le
 * pasan, al contrario de `contarEfectivo`, que suma todo lo que haya para no separarse del servidor.
 */
export function contarSolo(conteo: Record<string, number>, denominaciones: readonly number[]): number {
  return denominaciones.reduce((suma, d) => suma + d * (Number(conteo[String(d)]) || 0), 0);
}

/**
 * El conteo de una persona, de la denominación más grande a la más chica, hasta donde alcanzan; lo que
 * la moneda más chica no alcanza queda como `resto`. Cierra los días de Demo con lo esperado.
 */
export function contarComoCajero(
  monto: number,
  denominaciones: readonly number[]
): { conteo: Record<string, number>; resto: number } {
  const lista = [...denominaciones].sort((a, b) => b - a);
  if (lista.length === 0) throw new Error('No hay denominaciones para contar');
  let resto = Math.round(Number(monto));
  if (!Number.isFinite(resto) || resto < 0) throw new Error(`Monto que no se puede contar: ${monto}`);
  const conteo: Record<string, number> = {};
  for (const d of lista) {
    const n = Math.floor(resto / d);
    if (n > 0) {
      conteo[String(d)] = n;
      resto -= n * d;
    }
  }
  return { conteo, resto };
}

/**
 * Lo que ninguna combinación de billetes y monedas alcanza del esperado en efectivo: el residuo bajo la
 * moneda más chica, que dejan los impuestos ($21.590). Nadie lo puede contar, así que no es faltante.
 */
export function restoIncontable(esperado: number, denominaciones: readonly number[]): number {
  const menor = Math.min(...denominaciones);
  if (!Number.isFinite(menor) || menor <= 0) throw new Error('No hay denominaciones para contar');
  return ((Math.round(Number(esperado)) % menor) + menor) % menor;
}

/** Si la diferencia del efectivo es solo ese resto: el día cuadró hasta donde se puede contar. */
export function esRestoIncontable(diferencia: number, esperado: number, denominaciones: readonly number[]): boolean {
  const resto = restoIncontable(esperado, denominaciones);
  return resto > 0 && Math.round(Number(diferencia)) === -resto;
}

/**
 * Lo esperado en una cuenta: lo que había, más lo que entró, menos lo que salió. `gastos` son solo
 * los de esa cuenta; `salidas` y `entradas` son los dos lados de los traslados.
 */
export function esperadoDeCuenta({
  saldoInicial,
  ventas,
  gastos = 0,
  salidas = 0,
  entradas = 0,
}: {
  saldoInicial: number;
  ventas: number;
  gastos?: number;
  salidas?: number;
  entradas?: number;
}): number {
  return saldoInicial + ventas - gastos - salidas + entradas;
}
